import React, { useMemo, useState } from "react";
import { auth } from "../firebase";
import { API_BASE_URL, CLIENTS } from "../config";
import Sidebar from "./Sidebar";

function getTodayInputDate() {
  const today = new Date();
  const offset = today.getTimezoneOffset();
  return new Date(today.getTime() - offset * 60 * 1000).toISOString().slice(0, 10);
}

export default function TemporaryInvoice() {
  const requestedClient = new URLSearchParams(window.location.search).get("client");
  const [clientCode, setClientCode] = useState(
    CLIENTS.some((client) => client.code === requestedClient) ? requestedClient : CLIENTS[0].code
  );
  const [manualClientName, setManualClientName] = useState(
    () => new URLSearchParams(window.location.search).get("name") || ""
  );
  const [manualClientNTN, setManualClientNTN] = useState("");
  const [manualClientAddress, setManualClientAddress] = useState("");
  const [lineItems, setLineItems] = useState([
    { description: "", quantity: "", rate: "" },
    { description: "", quantity: "", rate: "" },
    { description: "", quantity: "", rate: "" }
  ]);
  const [invoiceNumber, setInvoiceNumber] = useState("");
  const [invoiceDate, setInvoiceDate] = useState(getTodayInputDate());
  const [purchaseOrder, setPurchaseOrder] = useState("");
  const [includeGst, setIncludeGst] = useState(false);
  const [gstOnServiceOnly, setGstOnServiceOnly] = useState(false);
  const [serviceChargesAmount, setServiceChargesAmount] = useState("");
  const [gstPercent, setGstPercent] = useState("16");
  const [loading, setLoading] = useState(false);

  const selectedClient = CLIENTS.find((client) => client.code === clientCode) || CLIENTS[0];
  const isTemporaryClient = clientCode === "TEMP";
  const invoiceClient = isTemporaryClient
    ? {
        code: "TEMP",
        name: manualClientName.trim(),
        ntn: manualClientNTN.trim(),
        address: manualClientAddress.trim()
      }
    : {
        ...selectedClient,
        ntn: selectedClient.gstNo
          ? `${selectedClient.ntn}, GST No. ${selectedClient.gstNo}`
          : selectedClient.ntn,
        address: selectedClient.address || ""
      };

  const subtotal = useMemo(
    () => lineItems.reduce(
      (sum, item) => sum + Math.round(Number(item.quantity || 0) * Number(item.rate || 0)),
      0
    ),
    [lineItems]
  );
  const serviceAmount = gstOnServiceOnly ? Math.round(Number(serviceChargesAmount || 0)) : 0;
  const invoiceSubtotal = subtotal + serviceAmount;
  const gstBase = includeGst ? subtotal : gstOnServiceOnly ? serviceAmount : 0;
  const gstAmount = Math.round(gstBase * (Number(gstPercent || 0) / 100));
  const invoiceTotal = invoiceSubtotal + gstAmount;

  const formattedAmount = useMemo(
    () => Math.round(invoiceTotal).toLocaleString("en-PK"),
    [invoiceTotal]
  );

  const updateLineItem = (index, field, value) => {
    setLineItems((items) =>
      items.map((item, itemIndex) =>
        itemIndex === index ? { ...item, [field]: value } : item
      )
    );
  };

  const addLineItem = () => {
    setLineItems((items) => [...items, { description: "", quantity: "", rate: "" }]);
  };

  const removeLineItem = (index) => {
    setLineItems((items) => items.filter((_, itemIndex) => itemIndex !== index));
  };

  const generateInvoice = async () => {
    const regularItems = lineItems
      .map((item) => ({
        description: item.description.trim(),
        quantity: Number(item.quantity || 0),
        rate: Number(item.rate || 0),
        amount: Math.round(Number(item.quantity || 0) * Number(item.rate || 0))
      }))
      .filter((item) => item.description || item.quantity || item.rate);

    const serviceCharge = Number(serviceChargesAmount);
    if (gstOnServiceOnly && (!Number.isFinite(serviceCharge) || serviceCharge <= 0)) {
      alert("Enter a service charges amount greater than zero.");
      return;
    }
    const invoiceItems = gstOnServiceOnly
      ? [...regularItems, { description: "Service Charges", quantity: 1, rate: serviceCharge, amount: serviceAmount }]
      : regularItems;

    const hasInvalidLineItem = invoiceItems.some(
      (item) =>
        !item.description ||
        !Number.isFinite(item.quantity) ||
        !Number.isFinite(item.rate) ||
        item.quantity <= 0 ||
        item.rate <= 0
    );

    if (!invoiceNumber.trim() || !invoiceClient.name || !invoiceItems.length) {
      alert("Please enter invoice number, client details, and at least one line item.");
      return;
    }

    if (hasInvalidLineItem) {
      alert("Each line item needs a description, quantity, and rate.");
      return;
    }

    const pdfSubtotal = invoiceItems.reduce((sum, item) => sum + item.amount, 0);
    const pdfGstBase = includeGst ? pdfSubtotal : gstOnServiceOnly ? serviceAmount : 0;
    const pdfGstAmount = Math.round(pdfGstBase * (Number(gstPercent) / 100));

    const user = auth.currentUser;

    if (!user) {
      alert("Not logged in.");
      return;
    }

    setLoading(true);

    try {
      const token = await user.getIdToken();
      const response = await fetch(`${API_BASE_URL}/invoices/download`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${token}`
        },
        body: JSON.stringify({
          invoiceType: "manual",
          invoiceNumber: invoiceNumber.trim(),
          invoiceDate,
          clientCode: invoiceClient.code,
          clientName: invoiceClient.name,
          clientNTN: invoiceClient.ntn,
          clientAddress: invoiceClient.address,
          purchaseOrder: purchaseOrder.trim(),
          invoiceTitle: includeGst || gstOnServiceOnly ? "Punjab Sales Tax Invoice" : "INVOICE",
          lineItems: invoiceItems,
          description: invoiceItems[0].description,
          amount: pdfSubtotal,
          subtotal: pdfSubtotal,
          gstPercent: includeGst || gstOnServiceOnly ? Number(gstPercent) : 0,
          taxLabel: gstOnServiceOnly ? "GST on Service Charges only" : "GST",
          gstAmount: pdfGstAmount,
          grandTotal: pdfSubtotal + pdfGstAmount
        })
      });

      if (!response.ok) {
        alert("PDF generation failed.");
        return;
      }

      const blob = await response.blob();
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement("a");

      a.href = url;
      a.download = `${invoiceNumber.replaceAll("/", "_")}.pdf`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      window.URL.revokeObjectURL(url);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="app-shell">
      <Sidebar />

      <main className="main-content">
        <header className="page-header">
          <div>
            <div className="eyebrow">Manual Billing</div>
            <h1 className="page-title">Temporary Invoice</h1>
            <p className="page-copy">
              Create a one-off PDF invoice with a manual invoice number, client name,
              description, and amount.
            </p>
          </div>
          <div className="metric-card" style={{ minWidth: 240 }}>
            <div className="metric-label">Invoice Value</div>
            <div className="metric-value">PKR {formattedAmount}</div>
            <div className="page-copy" style={{ marginTop: 8 }}>
              NTN: {invoiceClient.ntn || "Not provided"}
            </div>
          </div>
        </header>

        <section className="panel" style={{ maxWidth: 820 }}>
          <div className="panel-header">
            <div>
              <h2 className="panel-title">Temporary Invoice Details</h2>
              <p className="page-copy" style={{ marginTop: 4 }}>
                This PDF uses the invoice number you enter and will not override it.
              </p>
            </div>
            <span className="badge">PDF</span>
          </div>

          <div className="panel-body">
            <div className="form-grid">
              <div className="field">
                <label>Invoice Number</label>
                <input
                  className="input"
                  placeholder="e.g. TMP-001/2027"
                  value={invoiceNumber}
                  onChange={(e) => setInvoiceNumber(e.target.value)}
                />
              </div>

              <div className="field">
                <label>Invoice Date</label>
                <input
                  className="input"
                  type="date"
                  value={invoiceDate}
                  onChange={(e) => setInvoiceDate(e.target.value)}
                />
              </div>

              <div className="field">
                <label>P/O or Supply Order</label>
                <input
                  className="input"
                  placeholder="Enter P/O or supply order number"
                  value={purchaseOrder}
                  onChange={(e) => setPurchaseOrder(e.target.value)}
                />
              </div>

              <div className="field">
                <label>Client Name</label>
                <select
                  className="input"
                  value={clientCode}
                  onChange={(e) => setClientCode(e.target.value)}
                >
                  {CLIENTS.map((client) => (
                    <option key={client.code} value={client.code}>
                      {client.name}
                    </option>
                  ))}
                </select>
                <div className="page-copy" style={{ marginTop: 4 }}>
                  Client NTN: {invoiceClient.ntn || "Not provided"}
                </div>
                {invoiceClient.address && (
                  <div className="page-copy" style={{ marginTop: 4 }}>
                    Address: {invoiceClient.address}
                  </div>
                )}
              </div>

              <div className="field">
                <label>GST</label>
                <div style={{ display: "flex", gap: 10, alignItems: "center" }}>
                  <label style={{ display: "flex", alignItems: "center", gap: 8, fontWeight: 800 }}>
                    <input
                      type="checkbox"
                      checked={includeGst}
                      onChange={(e) => {
                        setIncludeGst(e.target.checked);
                        if (e.target.checked) setGstOnServiceOnly(false);
                      }}
                    />
                    Add GST
                  </label>
                </div>
                <label style={{ display: "flex", alignItems: "center", gap: 8, fontWeight: 800, marginTop: 10 }}>
                  <input
                    type="checkbox"
                    checked={gstOnServiceOnly}
                    onChange={(e) => {
                      setGstOnServiceOnly(e.target.checked);
                      if (e.target.checked) setIncludeGst(false);
                    }}
                  />
                  GST on service charges only
                </label>
                {(includeGst || gstOnServiceOnly) && (
                  <select
                    className="input"
                    aria-label="GST rate"
                    value={gstPercent}
                    onChange={(e) => setGstPercent(e.target.value)}
                    style={{ maxWidth: 140, marginTop: 10 }}
                  >
                    <option value="5">5%</option>
                    <option value="15">15%</option>
                    <option value="16">16%</option>
                    <option value="17">17%</option>
                    <option value="18">18%</option>
                  </select>
                )}
                <div className="page-copy" style={{ marginTop: 4 }}>
                  Subtotal: PKR {invoiceSubtotal.toLocaleString("en-PK")} | GST: PKR {gstAmount.toLocaleString("en-PK")} | Invoice Total: PKR {invoiceTotal.toLocaleString("en-PK")}
                </div>
              </div>
            </div>

            {gstOnServiceOnly && (
              <div className="invoice-card" style={{ boxShadow: "none", padding: 16, marginTop: 16 }}>
                <div className="form-grid">
                  <div className="field">
                    <label>Description</label>
                    <input className="input" value="Service Charges" readOnly />
                  </div>
                  <div className="field">
                    <label>Service Charges Amount (PKR)</label>
                    <input
                      className="input"
                      type="number"
                      min="0"
                      step="0.01"
                      placeholder="Enter amount subject to GST"
                      value={serviceChargesAmount}
                      onChange={(e) => setServiceChargesAmount(e.target.value)}
                    />
                  </div>
                </div>
                <p className="page-copy" style={{ marginTop: 8 }}>
                  Only this Service Charges line is subject to GST. Other invoice lines are excluded.
                </p>
              </div>
            )}

            {isTemporaryClient && (
              <div className="form-grid" style={{ marginTop: 16 }}>
                <div className="field">
                  <label>Temporary Client Name</label>
                  <input
                    className="input"
                    placeholder="Enter client or company name"
                    value={manualClientName}
                    onChange={(e) => setManualClientName(e.target.value)}
                  />
                </div>

                <div className="field">
                  <label>Temporary Client NTN</label>
                  <input
                    className="input"
                    placeholder="Enter NTN if available"
                    value={manualClientNTN}
                    onChange={(e) => setManualClientNTN(e.target.value)}
                  />
                </div>

                <div className="field" style={{ gridColumn: "1 / -1" }}>
                  <label>Temporary Client Address</label>
                  <textarea
                    className="textarea"
                    placeholder="Enter client address to print under the client name"
                    value={manualClientAddress}
                    onChange={(e) => setManualClientAddress(e.target.value)}
                  />
                </div>
              </div>
            )}

            <div style={{ marginTop: 20 }}>
              <div className="panel-header" style={{ padding: 0, borderBottom: 0, background: "transparent" }}>
                <div>
                  <h3 className="panel-title">Line Items</h3>
                  <p className="page-copy" style={{ marginTop: 4 }}>
                    Add description, quantity, and rate. Amount is calculated automatically.
                  </p>
                </div>
              </div>

              <div className="invoice-list" style={{ marginTop: 14 }}>
                {lineItems.map((item, index) => (
                  <div
                    key={index}
                    className="invoice-card"
                    style={{ boxShadow: "none", padding: 16 }}
                  >
                    <div className="line-item-grid">
                      <div className="field">
                        <label>Description</label>
                        <textarea
                          className="textarea"
                          placeholder="Describe this service, reimbursement, or adjustment"
                          value={item.description}
                          onChange={(e) => updateLineItem(index, "description", e.target.value)}
                        />
                      </div>

                      <div className="field">
                        <label>Qty</label>
                        <input
                          className="input"
                          type="number"
                          min="0"
                          step="0.01"
                          placeholder="0"
                          value={item.quantity}
                          onChange={(e) => updateLineItem(index, "quantity", e.target.value)}
                        />
                      </div>

                      <div className="field">
                        <label>Rate</label>
                        <input
                          className="input"
                          type="number"
                          min="0"
                          step="0.01"
                          placeholder="0"
                          value={item.rate}
                          onChange={(e) => updateLineItem(index, "rate", e.target.value)}
                        />
                      </div>

                      <div className="field">
                        <label>Amount</label>
                        <input
                          className="input"
                          value={Math.round(Number(item.quantity || 0) * Number(item.rate || 0)).toLocaleString("en-PK")}
                          readOnly
                        />

                        {lineItems.length > 3 && (
                          <button
                            type="button"
                            onClick={() => removeLineItem(index)}
                            className="btn btn-muted"
                            style={{ marginTop: 8 }}
                          >
                            Remove
                          </button>
                        )}
                      </div>
                    </div>
                  </div>
                ))}
              </div>

              <div className="button-row" style={{ justifyContent: "flex-start", marginTop: 14, paddingTop: 14 }}>
                <button
                  type="button"
                  onClick={addLineItem}
                  className="btn btn-muted"
                >
                  Add Line Item
                </button>
              </div>
            </div>

            <div className="button-row">
              <button
                type="button"
                onClick={generateInvoice}
                disabled={loading}
                className="btn btn-primary"
              >
                {loading ? "Generating PDF..." : "Generate PDF Invoice"}
              </button>
            </div>
          </div>
        </section>
      </main>
    </div>
  );
}
